"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const nearbyAreas = [{
  name: "High Wycombe",
  to: "/AreasWeCoverPage"
}, {
  name: "Beaconsfield",
  to: "/AreasWeCoverPage"
}, {
  name: "Berkshire",
  to: "/AreasWeCoverPage"
}, {
  name: "Surrey",
  to: "/AreasWeCoverPage"
}, {
  name: "Buckinghamshire",
  to: "/AreasWeCoverPage"
}];
export default function QuoteCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="quotecta" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="border border-border bg-card shadow-lg">
            <CardContent className="p-8 sm:p-12 lg:p-16">
              <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
                {/* Left Column: Heading and Description */}
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-xs font-semibold tracking-wide text-foreground uppercase mb-4">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>NASC & CHAS Accreditations</span>
                  </div>

                  <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
                    Request a scaffolding quote
                  </h2>

                  <p className="mt-4 text-base text-muted-foreground sm:text-lg max-w-2xl">
                    Get an accurate, safety-first proposal for your domestic, commercial, or specialist access project. Pete Wood and our experienced operations team are ready to assist.
                  </p>

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 font-semibold shadow-sm">
                      <Link to="/ContactRequestQuotePage">
                        <span>Get your quote online</span>
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="size-4 text-primary shrink-0" />
                      <span>Quotes returned promptly with clear pricing</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Contact Cards */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-5">
                  {/* Phone Block */}
                  <a href="tel:01344929900" className="group flex flex-col justify-between rounded-lg border border-border bg-background p-6 text-foreground transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                    <div>
                      <div className="flex size-10 items-center justify-center rounded-md bg-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <Phone className="size-5" />
                      </div>
                      <h3 className="mt-4 text-base font-semibold text-foreground">
                        Call our office
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Direct line for enquiries & site visits
                      </p>
                    </div>
                    <p className="mt-4 text-lg font-bold text-foreground group-hover:text-primary">
                      {"01344 929900"}
                    </p>
                  </a>

                  {/* Email Block */}
                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex flex-col justify-between rounded-lg border border-border bg-background p-6 text-foreground transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                    <div>
                      <div className="flex size-10 items-center justify-center rounded-md bg-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <Mail className="size-5" />
                      </div>
                      <h3 className="mt-4 text-base font-semibold text-foreground">
                        Send plans & specs
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Upload drawings and project details
                      </p>
                    </div>
                    <p className="mt-4 text-sm font-semibold break-all text-foreground group-hover:text-primary">
                      {"quotes@bmfscaffolding.co.uk"}
                    </p>
                  </a>
                </div>
              </div>

              <Separator className="my-8 lg:my-10" />

              {/* Nearby Areas Footer */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MapPin className="size-4 text-primary shrink-0" />
                  <span>Nearby areas covered:</span>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  {nearbyAreas.map((area, i) => <div key={area.name} data-index={i} className="flex items-center gap-4">
                      <Link to={area.to} className="text-foreground transition-colors hover:text-primary hover:underline underline-offset-4">
                        {area.name}
                      </Link>
                      {i < nearbyAreas.length - 1 && <span className="text-border select-none" aria-hidden="true">
                          {"•"}
                        </span>}
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}